import {
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  RangeSelection,
} from 'lexical';
import { $createLinkNode, $isLinkNode } from '@lexical/link';

// Links open in a new tab, and `noopener noreferrer` prevents the opened page
// from accessing `window.opener` and strips the referrer.
const LINK_TARGET = '_blank';
const LINK_REL = 'noopener noreferrer';

/**
 * Updates an existing link in place. Must be called inside `editor.update`.
 *
 * The link's URL and its first text child are mutated directly rather than
 * cleared/replaced: clearing the children leaves the inline link momentarily
 * empty and Lexical garbage-collects empty inline nodes mid-update, deleting
 * the link. Mutating the existing nodes keeps them attached and rendered.
 */
export function $editLinkByKey(
  key: string,
  linkText: string,
  url: string
): void {
  const node = $getNodeByKey(key);
  if (!$isLinkNode(node)) return;

  node.setURL(url);
  node.setTarget(LINK_TARGET);
  node.setRel(LINK_REL);

  // If the visible link text is unchanged, preserve existing children (and any
  // formatting) and only update the URL (while still enforcing target/rel).
  if (node.getTextContent() === linkText) {
    return;
  }

  const firstChild = node.getFirstChild();
  if ($isTextNode(firstChild)) {
    firstChild.setTextContent(linkText);
  } else {
    node.append($createTextNode(linkText));
  }

  // Drop any remaining children so the visible text matches `linkText`.
  node
    .getChildren()
    .slice(1)
    .forEach((child) => child.remove());
}

/**
 * Inserts a new link. Must be called inside `editor.update`.
 *
 * When `savedSelection` is a non-collapsed range it is restored first, so the
 * new link replaces the selected text (linkify). Otherwise the link is inserted
 * at the caret.
 */
export function $insertNewLink(
  linkText: string,
  url: string,
  savedSelection: RangeSelection | null
): void {
  if (savedSelection) {
    $setSelection(savedSelection.clone());
  }

  const linkNode = $createLinkNode(url, {
    target: LINK_TARGET,
    rel: LINK_REL,
  });
  linkNode.append($createTextNode(linkText));

  const selection = $getSelection();
  if ($isRangeSelection(selection) && !selection.isCollapsed()) {
    selection.insertNodes([linkNode]);
  } else {
    $insertNodes([linkNode]);
  }
}
